/**
 * Number / currency formatters shared by chart hover panels and trend cards.
 *
 * Two distinct domains live here:
 *   - **UZS amounts** stored as tiyin (1/100 so'm) on the wire, displayed
 *     without a suffix (chart axes, tooltips, stat tiles).
 *   - **Counts** (whole-number sales/changes/orgs).
 *
 * Helpers gracefully accept null/undefined/empty and return "-" so callers
 * don't have to repeat the guard.
 */

const UZS_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const UZS_NUMBER_FMT_NO_DECIMAL = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const UZS_SIGNED_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  signDisplay: "exceptZero",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

const COUNT_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const COUNT_SIGNED_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  signDisplay: "exceptZero",
  maximumFractionDigits: 0,
});

const PERCENT_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  signDisplay: "exceptZero",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/** Convert tiyin (1/100 UZS) to UZS, returning null on invalid input. */
export function tiyinToUzs(value: number | string | null | undefined): number | null {
  if (value == null || value === "") return null;
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  return numeric / 100;
}

type MoneyFormatOptions = {
  noDecimals?: boolean;
};

const getUzsFormatter = ({ noDecimals }: MoneyFormatOptions = {}) =>
  noDecimals ? UZS_NUMBER_FMT_NO_DECIMAL : UZS_NUMBER_FMT;

/** Format a tiyin amount as a UZS string ("44,799"). */
export function fmtUzs(tiyin: number | string | null | undefined, options: MoneyFormatOptions = {}): string {
  const value = tiyinToUzs(tiyin);
  if (value == null) return "-";
  return getUzsFormatter(options).format(value);
}

/** Format a tiyin amount with explicit sign ("+44,799" / "-1,200"). */
export function fmtSignedUzs(tiyin: number | string | null | undefined): string {
  const value = tiyinToUzs(tiyin);
  if (value == null) return "-";
  return UZS_SIGNED_NUMBER_FMT.format(value);
}

/** Format an already-UZS numeric value (no tiyin conversion). */
export function formatUzsValue(value: number | null | undefined, options: MoneyFormatOptions = {}): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return getUzsFormatter(options).format(value);
}

/** Format a whole-number count with thousand separators. */
export function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return COUNT_NUMBER_FMT.format(value);
}

/** Format a whole-number count with explicit sign ("+12" / "-3"). */
export function formatSignedCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return COUNT_SIGNED_NUMBER_FMT.format(value);
}

/** Compute relative percent diff from `base` to `value`. */
export function computePercent(value: number, base: number | null | undefined): number | null {
  if (base == null || base === 0 || !Number.isFinite(value)) return null;
  return ((value - base) / base) * 100;
}

const GENERAL_NUMBER_FMT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

/** Format a number with up to 1 decimal place and thousand separators. */
export function formatNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return GENERAL_NUMBER_FMT.format(value);
}

/** Format a percent value as "+12%" / "-3%" / "0%". */
export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  return `${PERCENT_NUMBER_FMT.format(Math.round(value))}%`;
}

export function formatMarkup(value: number | string | null | undefined, locale: string): string {
  if (value == null || value === "") return "-";
  const numeric = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `${new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(numeric)}%`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
